<template>
  <!-- Design-system look: a second navigation column between the sidebar and the page, headed by
       an illustration, a title and a subtitle, its rows styled like the App Builder's page list
       (outlined rows, the active one on the soft brand surface with a solid icon chip). Its header
       is exactly as tall as the sidebar's logo row, so both dividers line up. Only
       presents what it is given: the event sections (SectionNav) and the user settings
       (SettingsTabContainer) supply the pages. From lg up; it flies in when it mounts. -->
  <!-- z-10: the fold button below hangs half over the page, and <main> is positioned and comes
       later in the markup, so without a stacking order of its own the column loses that half to
       <main> and the button is only clickable on the side that sits over the column itself. -->
  <aside
    class="ds-section-nav sticky top-0 z-10 hidden h-body w-[248px] flex-none flex-col border-r border-(--ds-border-app) bg-(--ds-surface-default) lg:flex"
    :aria-label="title"
    data-test="section-nav"
  >
    <!-- Folds the column away, like the sidebar's own toggle; picking the section in the sidebar
         brings it back. -->
    <button
      v-if="collapsible"
      type="button"
      class="absolute top-3 -right-3.5 z-2 flex size-7 items-center justify-center rounded-full border border-(--ds-border-app) bg-(--ds-surface-default) text-(--ds-text-secondary) transition-colors duration-150 hover:border-(--ds-border-brand) hover:text-(--ds-text-brand)"
      :aria-label="$t('common.collapse_menu')"
      :title="$t('common.collapse_menu')"
      data-test="section-nav-collapse"
      @click="emit('collapse')"
    >
      <!-- eslint-disable vue/no-v-html -- a static design-system icon -->
      <span
        class="flex size-3.5 [&>svg]:size-full"
        aria-hidden="true"
        v-html="svg('chevron-left')"
      />
      <!-- eslint-enable vue/no-v-html -->
    </button>

    <header
      class="flex h-[68px] flex-none items-center gap-3 border-b border-(--ds-border-subtle) pr-4 pl-5"
    >
      <img
        v-if="illustration"
        :src="Vapor.asset(illustration)"
        alt=""
        aria-hidden="true"
        class="size-9 flex-none"
      />
      <div class="min-w-0">
        <p
          class="truncate text-[15px] leading-tight font-semibold text-(--ds-text-primary)"
          data-test="section-nav-title"
          v-text="title"
        />
        <p
          v-if="subtitle"
          class="mt-0.5 truncate text-[12.5px] text-(--ds-text-secondary)"
          v-text="subtitle"
        />
      </div>
    </header>

    <!-- The page icons are the design system's static SVGs (ICONS), never user data. -->
    <!-- eslint-disable vue/no-v-html -->
    <nav class="flex flex-1 flex-col gap-1.5 overflow-y-auto px-3 pt-3.5 pb-4">
      <template v-for="page in pages" :key="page.key">
        <!-- A heading that groups the rows after it (the Marketplace's „Lösungen"). -->
        <p
          v-if="page.heading"
          class="px-1 pt-3 pb-0.5 text-[11px] font-bold tracking-[0.08em] text-(--ds-text-secondary) uppercase"
          :data-test="`section-nav-${page.key}`"
          v-text="page.heading"
        />
        <!-- A page that cannot be opened yet (the event app's pages before the app exists). -->
        <span
          v-else-if="page.locked"
          class="flex min-h-11 cursor-not-allowed items-center gap-2.5 rounded-(--ds-radius-md) px-2.5 text-[14px] font-medium text-(--ds-text-secondary) opacity-45 shadow-[inset_0_0_0_1px_var(--ds-border-app)]"
          aria-disabled="true"
          :title="page.hint ?? undefined"
          :data-test="`section-nav-${page.key}`"
        >
          <span
            class="flex size-[26px] flex-none items-center justify-center [&>svg]:size-4"
            aria-hidden="true"
            v-html="svg(page.icon)"
          />
          <span class="py-2 leading-snug" v-text="page.label" />
        </span>
        <Link
          v-else
          :href="page.href"
          class="flex min-h-11 items-center gap-2.5 rounded-(--ds-radius-md) px-2.5 text-[14px] font-medium no-underline transition-[background-color,box-shadow,color] duration-150"
          :class="
            page.active
              ? 'bg-(--ds-surface-brand) text-(--ds-text-brand) shadow-[inset_0_0_0_1px_var(--ds-border-brand)]'
              : 'bg-(--ds-surface-default) text-(--ds-text-primary) shadow-[inset_0_0_0_1px_var(--ds-border-app)] hover:shadow-[inset_0_0_0_1px_var(--ds-border-strong)]'
          "
          :aria-current="page.active ? 'page' : undefined"
          :title="page.hint ?? undefined"
          :data-test="`section-nav-${page.key}`"
        >
          <img
            v-if="page.image"
            :src="page.image"
            alt=""
            class="size-[26px] flex-none"
          />
          <span
            v-else
            class="flex size-[26px] flex-none items-center justify-center rounded-(--ds-radius-sm) transition-colors duration-150 [&>svg]:size-4"
            :class="
              page.active
                ? 'bg-(--ds-action-brand) text-(--ds-color-white)'
                : 'text-(--ds-text-secondary)'
            "
            aria-hidden="true"
            v-html="svg(page.icon)"
          />
          <span class="py-2 leading-snug" v-text="page.label" />
          <!-- „Premium" on an Event App function not booked for the event (R7 part C). -->
          <span
            v-if="page.badge"
            class="ml-auto shrink-0 rounded-(--ds-radius-pill) bg-(--ds-surface-brand) px-2 py-0.5 text-[11px] font-semibold text-(--ds-text-brand)"
            :data-test="`section-nav-${page.key}-badge`"
            v-text="page.badge"
          />
        </Link>
        <!-- Package 22C (N1): the sub-areas the open page registered (composables/pageAreas.js),
             indented under its row; each one switches the page's area and its URL parameter. -->
        <div
          v-if="!page.locked && page.key === areasPageKey"
          role="group"
          class="ml-[22px] flex flex-col gap-0.5 border-l border-(--ds-border-subtle) pl-2"
          :aria-label="areas.label"
          data-test="section-nav-areas"
        >
          <button
            v-for="item in areas.items"
            :key="item.key"
            type="button"
            class="flex min-h-9 w-full items-center justify-between gap-3 rounded-(--ds-radius-md) px-2.5 py-1.5 text-left text-[13.5px] transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-focus"
            :class="
              areas.active === item.key
                ? 'bg-(--ds-surface-brand) font-semibold text-(--ds-text-brand)'
                : 'font-medium text-(--ds-text-secondary) hover:bg-(--ds-surface-app) hover:text-(--ds-text-primary)'
            "
            :aria-current="areas.active === item.key ? 'true' : undefined"
            :data-test="`section-nav-area-${item.key}`"
            @click="areas.select(item.key)"
          >
            <span class="leading-snug" v-text="item.label" />
            <span
              v-if="item.count !== undefined && item.count !== null"
              class="flex-none text-xs tabular-nums"
              data-test="section-nav-area-count"
              v-text="item.count"
            />
          </button>
        </div>
      </template>
      <!-- A section may end the column with an action below a separator (the planner's video). -->
      <template v-if="$slots.after">
        <div
          class="mx-1 my-1.5 h-px flex-none bg-(--ds-border-subtle)"
          role="separator"
        />
        <slot name="after" />
      </template>
    </nav>
    <!-- eslint-enable vue/no-v-html -->
  </aside>
</template>

<script setup>
import { ICONS } from '@/Libraries/design-system-icons'
import { pageAreas, setPageAreasHost } from '@/composables/pageAreas'
import { Link } from '@inertiajs/vue3'
import Vapor from 'laravel-vapor'
import { computed, onBeforeUnmount, watchEffect } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: [String, null], default: null },
  /** Asset path of the header illustration (public/images/ds-illustrations). */
  illustration: { type: [String, null], default: null },
  /**
   * `icon` is a design-system icon name (Libraries/design-system-icons.js).
   *
   * `component` is the Inertia page component the row opens; a page that registers its areas
   * (pageAreas.js) gets them under the row whose `component` or `key` matches.
   *
   * `heading` makes the entry a group heading (no link); `image` is a coloured illustration URL
   * shown instead of the icon; `badge` is a short chip after the label („Premium").
   *
   * @type {Array<{key: string, component?: string, label?: string, href?: string, icon?: string|null, image?: string, heading?: string, badge?: string|null, active?: boolean, locked?: boolean}>}
   */
  pages: { type: Array, required: true },
  /** Shows the round fold button on the column's edge. */
  collapsible: { type: Boolean, default: false },
})

const emit = defineEmits(['collapse'])

const svg = (name) => ICONS[name] ?? ICONS.doc

const areas = pageAreas

/** The key of the row the registered areas belong to, or null. */
const areasPageKey = computed(() => {
  const pageKey = areas.value?.pageKey ?? null

  if (pageKey === null) {
    return null
  }

  return (
    props.pages.find(
      (page) =>
        !page.locked && (page.component === pageKey || page.key === pageKey),
    )?.key ?? null
  )
})

/** Tells the page that this column shows its areas, so it hides its own tab row from lg up. */
const host = Symbol('section-nav-column')

watchEffect(() => setPageAreasHost(host, areasPageKey.value !== null))

onBeforeUnmount(() => setPageAreasHost(host, false))
</script>
